/** UI preset list; stored in DB as `customerOfferHighlight: string[]`. */

export const CUSTOMER_OFFER_PRESET_LABELS = [
  "Product Demo / Presentation",
  "Trial Period",
  "Technical Consultation",
  "Integration Support",
  "Custom Solution Development",
  "Process Optimization Support",
  "Training & Onboarding",
  "After-Sales Support",
  "Other",
] as const;

export const CUSTOMER_OFFER_OTHER_LABEL = "Other" as const;

export const CUSTOMER_OFFER_OTHER_TEXT_MAX_LENGTH = 30;

export function truncateCustomerOfferOtherText(text: string): string {
  return String(text ?? "").slice(0, CUSTOMER_OFFER_OTHER_TEXT_MAX_LENGTH);
}

/** Legacy label stored before rename to "Trial". */
const CUSTOMER_OFFER_LEGACY_TRIAL_LABEL = "Pilot Project / Trial";

const NON_OTHER_PRESETS = CUSTOMER_OFFER_PRESET_LABELS.filter((l) => l !== CUSTOMER_OFFER_OTHER_LABEL);

export type CustomerOfferPresetLabel = (typeof CUSTOMER_OFFER_PRESET_LABELS)[number];

export type CustomerOfferHighlightApi = string | string[] | null | undefined;

/** Normalize API / legacy scalar into a string array. */
export function normalizeCustomerOfferHighlightFromApi(
  stored: CustomerOfferHighlightApi,
): string[] {
  if (stored == null) return [];
  if (Array.isArray(stored)) {
    return stored.map((s) => String(s ?? "").trim()).filter(Boolean);
  }
  const s = String(stored).trim();
  return s ? [s] : [];
}

/** Parse one stored entry (preset label or custom “Other” text). */
function parseSingleCustomerOfferEntry(stored: string): { preset: string; otherText: string } {
  const s = (stored ?? "").trim();
  if (!s) return { preset: "", otherText: "" };
  if (s === CUSTOMER_OFFER_LEGACY_TRIAL_LABEL) {
    return { preset: "Trial", otherText: "" };
  }
  if ((NON_OTHER_PRESETS as readonly string[]).includes(s)) {
    return { preset: s, otherText: "" };
  }
  if (s === CUSTOMER_OFFER_OTHER_LABEL) {
    return { preset: CUSTOMER_OFFER_OTHER_LABEL, otherText: "" };
  }
  return { preset: CUSTOMER_OFFER_OTHER_LABEL, otherText: s };
}

/** Split API values into multi-select UI fields. */
export function parseCustomerOfferHighlightsFromApi(stored: CustomerOfferHighlightApi): {
  presets: string[];
  otherText: string;
} {
  const values = normalizeCustomerOfferHighlightFromApi(stored);
  if (values.length === 0) return { presets: [], otherText: "" };

  const presets: string[] = [];
  let otherText = "";

  for (const raw of values) {
    const { preset, otherText: entryOther } = parseSingleCustomerOfferEntry(raw);
    if (!preset) continue;
    if (preset === CUSTOMER_OFFER_OTHER_LABEL) {
      if (!presets.includes(CUSTOMER_OFFER_OTHER_LABEL)) presets.push(CUSTOMER_OFFER_OTHER_LABEL);
      if (entryOther.trim()) otherText = truncateCustomerOfferOtherText(entryOther.trim());
    } else if (!presets.includes(preset)) {
      presets.push(preset);
    }
  }

  return { presets, otherText };
}

/** Values sent to API: preset labels and custom text when "Other" is chosen. */
export function serializeCustomerOfferHighlightsForApi(
  presets: string[],
  otherText: string,
): string[] {
  const selected = (presets ?? []).map((p) => p.trim()).filter(Boolean);
  if (selected.length === 0) return [];

  const out: string[] = [];
  for (const p of selected) {
    if (p === CUSTOMER_OFFER_OTHER_LABEL) {
      const t = truncateCustomerOfferOtherText(otherText ?? "").trim();
      if (t) out.push(t);
    } else {
      out.push(p);
    }
  }
  return out;
}

/** @deprecated Use `parseCustomerOfferHighlightsFromApi` — first selection only. */
export function parseCustomerOfferHighlightFromApi(stored: CustomerOfferHighlightApi): {
  preset: string;
  otherText: string;
} {
  const { presets, otherText } = parseCustomerOfferHighlightsFromApi(stored);
  return { preset: presets[0] ?? "", otherText };
}

/** @deprecated Use `serializeCustomerOfferHighlightsForApi`. */
export function serializeCustomerOfferHighlightForApi(
  preset: string,
  otherText: string,
): string[] {
  const p = (preset ?? "").trim();
  return serializeCustomerOfferHighlightsForApi(p ? [p] : [], otherText);
}
